import { useEffect, useState } from "react";
import { BadgeCheck, Check, CreditCard, Download, Receipt, RefreshCw } from "lucide-react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import PaymentForm from "@/components/PaymentForm";
import { toast } from "sonner";

import { PLANS, PLAN_ORDER, COMPARISON_ROWS } from "@/lib/plans";

const PLAN_RANK = Object.fromEntries(PLAN_ORDER.map((p, i) => [p, i]));
const PLAN_PRICE = Object.fromEntries(PLAN_ORDER.map((p) => [p, PLANS[p].price]));
const PLAN_INFO = PLANS;
const STATUS_BADGE = {
  active: { label: "نشط", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  expired: { label: "منتهي", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  pending_payment: { label: "بانتظار الدفع", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  payment_verification: { label: "التحقق من الدفع", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_verification: { label: "بانتظار توثيق البريد", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  disabled: { label: "موقوف", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};
const INV_STATUS = {
  paid: { label: "مدفوعة", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "قيد المراجعة", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "مدفوعة", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "—");

export default function MySubscriptionTab() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loadingInv, setLoadingInv] = useState(true);
  const [payPlan, setPayPlan] = useState(null);

  useEffect(() => {
    api.get("/payments/invoices")
      .then((r) => setInvoices(r.data))
      .catch(() => {})
      .finally(() => setLoadingInv(false));
  }, []);

  const plan = user?.plan || "trial";
  const info = PLAN_INFO[plan] || PLAN_INFO.trial;
  const badge = STATUS_BADGE[user?.plan_status] || STATUS_BADGE.active;
  const isActive = user?.plan_status === "active";
  const curPrice = PLAN_PRICE[plan] ?? 0;
  const upgradePrice = (p) => (isActive ? Math.max(PLAN_PRICE[p] - curPrice, 0) : PLAN_PRICE[p]);

  const downloadInvoice = async (inv) => {
    try {
      await downloadFile(`/payments/invoices/${inv.kind}/${inv.id}/pdf`,
        `فاتورة-${fmtDate(inv.created_at)}.pdf`);
      toast.success("تم تحميل الفاتورة");
    } catch { toast.error("تعذر تحميل الفاتورة"); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="my-subscription-tab">
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border shadow-sm p-6" data-testid="current-plan-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-emerald-900 flex items-center justify-center">
              <BadgeCheck className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="font-bold">اشتراكي الحالي</h3>
              <p className="text-xs text-slate-400">تفاصيل باقتك وحالتها</p>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between border-b pb-3">
              <span className="text-slate-500">الباقة</span>
              <span className="font-bold text-emerald-950" data-testid="current-plan-name">{info.name}</span>
            </div>
            <div className="flex items-center justify-between border-b pb-3">
              <span className="text-slate-500">الحالة</span>
              <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-0.5 ${badge.cls}`}
                data-testid="current-plan-status">{badge.label}</span>
            </div>
            <div className="flex items-center justify-between border-b pb-3">
              <span className="text-slate-500">تاريخ البداية</span>
              <span className="font-mono text-xs" dir="ltr" data-testid="current-plan-start">{fmtDate(user?.subscription_start)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">تاريخ الانتهاء</span>
              <span className="font-mono text-xs" dir="ltr" data-testid="current-plan-end">{fmtDate(user?.subscription_end)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-6" data-testid="plans-comparison-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-900 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="font-bold">مقارنة الباقات</h3>
              <p className="text-xs text-slate-400">اختر الباقة المناسبة لمدرستك — التفعيل فوري عند الدفع</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="plans-comparison-table">
              <thead>
                <tr>
                  <th className="text-right px-2 py-2 font-semibold text-slate-500 min-w-[150px]">الميزة</th>
                  {PLAN_ORDER.map((p) => (
                    <th key={p} data-testid={`cmp-plan-head-${p}`}
                      className={`px-2 py-2 text-center font-bold whitespace-nowrap ${
                        p === plan ? "bg-emerald-50 text-emerald-900 rounded-t-xl border-x-2 border-t-2 border-emerald-200" : "text-slate-600"}`}>
                      {PLANS[p].name}
                      <span className={`block text-[10px] font-semibold mt-0.5 ${p === plan ? "text-emerald-700" : "text-slate-400"}`}>
                        {PLANS[p].priceLabel} / {PLANS[p].duration}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map(([label, ...vals], i) => (
                  <tr key={label} className={i % 2 ? "bg-slate-50/70" : ""} data-testid={`cmp-row-${i}`}>
                    <td className="px-2 py-2 text-right text-slate-600">{label}</td>
                    {PLAN_ORDER.map((p, j) => (
                      <td key={p} className={`px-2 py-2 text-center ${p === plan ? "bg-emerald-50 border-x-2 border-emerald-200" : ""}`}>
                        {vals[j]
                          ? <Check className="w-4 h-4 text-emerald-700 mx-auto" data-testid={`cmp-yes-${i}-${p}`} />
                          : <span className="text-slate-300" data-testid={`cmp-no-${i}-${p}`}>—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-2 py-3" />
                  {PLAN_ORDER.map((p) => (
                    <td key={p} className={`px-2 py-3 text-center ${p === plan ? "bg-emerald-50 rounded-b-xl border-x-2 border-b-2 border-emerald-200" : ""}`}>
                      {p === plan ? (
                        <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-full px-2.5 py-1"
                          data-testid={`cmp-current-badge-${p}`}>باقتك الحالية</span>
                      ) : p === "trial" || PLAN_RANK[p] <= (PLAN_RANK[plan] ?? 0) ? (
                        <span className="text-[10px] text-slate-300">—</span>
                      ) : (
                        <button type="button" onClick={() => setPayPlan(p)}
                          data-testid={`cmp-select-plan-${p}`}
                          className={`text-[11px] font-bold rounded-lg px-3 py-1.5 transition-colors ${
                            payPlan === p ? "bg-emerald-800 text-white" : "bg-emerald-900/10 text-emerald-900 hover:bg-emerald-900/20"}`}>
                          ترقية
                        </button>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          {payPlan && (
            <div className="mt-4 border-t pt-4" data-testid="plan-payment-section">
              <p className="text-xs text-slate-500 mb-3 flex items-center gap-1.5" data-testid="plan-payment-label">
                <CreditCard className="w-4 h-4" />
                الدفع الآمن عبر ميسر — {PLANS[payPlan].name}
                {isActive && PLAN_RANK[payPlan] > (PLAN_RANK[plan] ?? 0) && upgradePrice(payPlan) < PLANS[payPlan].price
                  ? ` (${upgradePrice(payPlan)} ريال بعد خصم قيمة باقتك الحالية)`
                  : ` (${PLANS[payPlan].priceLabel})`}
              </p>
              <PaymentForm key={payPlan} plan={payPlan} />
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border shadow-sm p-6" data-testid="invoices-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-900 flex items-center justify-center">
            <Receipt className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="font-bold">سجل الفواتير</h3>
            <p className="text-xs text-slate-400">فواتيرك السابقة والحالية مع إمكانية التحميل</p>
          </div>
        </div>
        <div className="space-y-2" data-testid="invoices-list">
          {loadingInv && <p className="text-sm text-slate-400">جارٍ التحميل...</p>}
          {!loadingInv && invoices.length === 0 && (
            <p className="text-sm text-slate-400" data-testid="invoices-empty">لا توجد فواتير بعد</p>
          )}
          {invoices.map((inv) => {
            const st = INV_STATUS[inv.status] || INV_STATUS.paid;
            return (
              <div key={`${inv.kind}-${inv.id}`} data-testid={`invoice-row-${inv.kind}-${inv.id}`}
                className="flex items-center justify-between border rounded-xl px-4 py-3 gap-2">
                <div>
                  <p className="text-sm font-semibold">{inv.plan_label}</p>
                  <p className="text-[11px] text-slate-400">
                    <span dir="ltr" className="font-mono">{fmtDate(inv.created_at)}</span>
                    {" — "}{inv.kind === "card" ? "بطاقة إلكترونية" : "تحويل بنكي"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-left">
                    <p className="text-sm font-bold text-emerald-800">{inv.amount} ريال</p>
                    <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${st.cls}`}
                      data-testid={`invoice-status-${inv.kind}-${inv.id}`}>{st.label}</span>
                  </div>
                  <button onClick={() => downloadInvoice(inv)}
                    data-testid={`btn-download-invoice-${inv.kind}-${inv.id}`}
                    title="تحميل الفاتورة PDF"
                    className="text-slate-400 hover:text-emerald-800 p-1.5 transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
